import {
  ingredientDryYeast,
  ingredientFlour,
  ingredientFreshYeast,
  ingredientLevain, ingredientTypeGrain, ingredientTypeInclusion, ingredientTypeLiquid, ingredientTypeTopping,
  ingredientUnknown,
  ingredientWater
} from "./Types";
import {toFixedDecimal} from "../utils/NumberUtils";

export const unitKilos = 'kilogram'
export const unitGrams = 'gram'
export const unitLiters = 'liter'
export const unitMillis = 'milliliter'
export const unitPounds = 'pound'
export const unitOunces = 'ounce'
export const unitPercent = 'percent'
export const unitCups = 'cup'
export const unitTablespoon = 'tablespoon'
export const unitTeaspoon = 'teaspoon'

export const unitFpUnit = 'fpUnit'
export const unitFpTotal = 'fpTotal'

export const FLOUR_EXCLUSION_UNITS = [unitPercent, unitLiters, unitMillis]
export const LIQUID_EXCLUSION_UNITS = [unitPercent]
export const GENERAL_DRY_EXCLUSION_UNITS = [unitPercent, unitLiters, unitMillis, unitCups, unitTeaspoon, unitTablespoon]

export const unitMap = [
  {
    unitType: unitCups,
    unitLabel: 'c'
  },
  {
    unitType: unitGrams,
    unitLabel: 'g'
  },
  {
    unitType: unitKilos,
    unitLabel: 'kg'
  },
  {
    unitType: unitLiters,
    unitLabel: 'l'
  },
  {
    unitType: unitMillis,
    unitLabel: 'ml'
  },
  {
    unitType: unitOunces,
    unitLabel: 'oz'
  },
  {
    unitType: unitPercent,
    unitLabel: '%'
  },
  {
    unitType: unitPounds,
    unitLabel: 'lb'
  },
  {
    unitType: unitTablespoon,
    unitLabel: 'T'
  },
  {
    unitType: unitTeaspoon,
    unitLabel: 't'
  }
]

export const fixedPriceUnitMap = [
  {
    unitType: unitFpUnit,
    unitLabel: 'u'
  },
  {
    unitType: unitFpTotal,
    unitLabel: 't'
  }
]

// conversion factors
const dryWeightOunceToGram = 28.349523
const dryWeightPoundToGram = 453.59237
const dryWeightKgToGram = 1000

const flourCupToGram = 125
const flourTbspToGram = 7.83
const flourTspToGram = 2.61

const dryYeastTbspToGram = 9
const dryYeastTspToGram = 3

const waterKgToGram = 1000
const waterLiterToGram = 1000
const waterPoundToGram = 453.59237
const waterOunceToGram = 29.5735
const waterCupToGram = 236.588
const waterTbspToGram = 14.786765
const waterTspToGram = 4.928922


export function getUnitLabel(unitName) {
  let unit = unitMap.find((u) => u.unitType === unitName)
  if (unit === undefined) {
    unit = fixedPriceUnitMap.find((u) => u.unitType === unitName)
    if (unit === undefined) {
      return '?'
    }
  }
  return unit.unitLabel
}

// OK: convert an ingredient from a weight in grams to a weight in <unitType>
export function convertGramsToUnits(amount, destUnitType, ingredientName) {
  if (unitGrams === destUnitType) {
    return amount
  }
  switch(ingredientName) {
    case ingredientFlour:
    case ingredientTypeGrain:
      return flourGramsToUnit(amount, destUnitType)
    case ingredientWater:
    case ingredientTypeLiquid:
      return waterGramsToUnit(amount, destUnitType)
    case ingredientDryYeast:
      return dryYeastGramsToUnit(amount, destUnitType)
    case ingredientUnknown:
    case ingredientTypeInclusion:
    case ingredientTypeTopping:
      return dryWeightGramsToUnit(amount, destUnitType)
    default: {
      return NaN
    }
  }
}

// OK: convert an ingredient from a weight in <unitType> to a weight in grams
export function convertUnitToGrams(amount, srcUnitType, ingredientName) {
  if (srcUnitType === unitGrams) {
    return amount
  }
  switch(ingredientName) {
    case ingredientFlour:
    case ingredientTypeGrain:
      return Math.round(flourUnitToGrams(amount, srcUnitType))
    case ingredientWater:
    case ingredientTypeLiquid:
      return Math.round(waterUnitToGrams(amount, srcUnitType))
    case ingredientDryYeast:
      return toFixedDecimal(dryYeastUnitToGrams(amount, srcUnitType), 1)
    case ingredientFreshYeast:
      return Math.round(dryYeastUnitToGrams(amount, srcUnitType) * 2)
    case ingredientLevain:
      return Math.round(levainUnitToGrams(amount, srcUnitType))
    case ingredientUnknown:
    case ingredientTypeInclusion:
    case ingredientTypeTopping:
      return Math.round(dryWeightUnitToGrams(amount, srcUnitType))
    default: {
      return NaN
    }
  }
}

// OK: convert flour from a weight in <unitType> to a weight in grams
export function flourUnitToGrams(amount, srcUnitType) {
  // valid units: grams, cups, oz, kilos, lbs, tsp, tbsp
  switch(srcUnitType) {
    case unitGrams:
      return amount
    case unitCups:
      return amount * flourCupToGram
    case unitOunces:
      return amount * dryWeightOunceToGram
    case unitKilos:
      return amount * dryWeightKgToGram
    case unitPounds:
      return amount * dryWeightPoundToGram
    case unitTeaspoon:
      return amount * flourTspToGram
    case unitTablespoon:
      return amount * flourTbspToGram
    default:
      return NaN
  }
}

// OK: convert flour from a weight in grams to a weight in <unitType>
export function flourGramsToUnit(amount, destUnitType) {
  // valid units: grams, cups, oz, kilos, lbs, tsp, tbsp
  switch(destUnitType) {
    case unitGrams:
      return amount
    case unitCups:
      return (amount / flourCupToGram)
    case unitOunces:
      return (amount / dryWeightOunceToGram)
    case unitKilos:
      return (amount / dryWeightKgToGram)
    case unitPounds:
      return (amount / dryWeightPoundToGram)
    case unitTeaspoon:
      return (amount / flourTspToGram)
    case unitTablespoon:
      return (amount / flourTbspToGram)
    default:
      return NaN
  }
}

// OK: convert water from a weight in <unitType> to a weight in grams
export function waterUnitToGrams(amount, srcUnitType) {
  // valid units: grams, kilos, litres, millis, oz, lbs, cups, tsp, tbsp
  switch(srcUnitType) {
    case unitGrams:
    case unitMillis:
      return amount
    case unitCups:
      return amount * waterCupToGram
    case unitOunces:
      return amount * waterOunceToGram
    case unitKilos:
      return amount * waterKgToGram
    case unitLiters:
      return amount * waterLiterToGram
    case unitPounds:
      return amount * waterPoundToGram
    case unitTeaspoon:
      return amount * waterTspToGram
    case unitTablespoon:
      return amount * waterTbspToGram
    default:
      return NaN
  }
}

// OK: convert water from a weight in grams to a weight in <unitType>
export function waterGramsToUnit(amount, destUnitType) {
  // valid units: grams, kilos, litres, millis, oz, lbs, cups, tsp, tbsp
  switch(destUnitType) {
    case unitGrams:
    case unitMillis:
      return Math.round(amount)
    case unitCups:
      return (amount / waterCupToGram)
    case unitOunces:
      return (amount / waterOunceToGram)
    case unitKilos:
      return (amount / waterKgToGram)
    case unitLiters:
      return (amount / waterLiterToGram)
    case unitPounds:
      return (amount / waterPoundToGram)
    case unitTeaspoon:
      return (amount / waterTspToGram)
    case unitTablespoon:
      return (amount / waterTbspToGram)
    default:
      return NaN
  }
}

// OK: convert dry yeast from a weight in <unitType> to a weight in grams
export function dryYeastUnitToGrams(amount, srcUnitType) {
  // valid units: grams, oz, kilos, lbs, tsp, tbsp
  switch(srcUnitType) {
    case unitGrams:
      return amount
    case unitOunces:
      return amount * dryWeightOunceToGram
    case unitKilos:
      return amount * dryWeightKgToGram
    case unitPounds:
      return amount * dryWeightPoundToGram
    case unitTeaspoon:
      return amount * dryYeastTspToGram
    case unitTablespoon:
      return amount * dryYeastTbspToGram
    default:
      return NaN
  }
}

// OK: convert dry yeast from a weight in grams to a weight in <unitType>
export function dryYeastGramsToUnit(amount, destUnitType) {
  // valid units: grams, oz, kilos, lbs, tsp, tbsp
  switch(destUnitType) {
    case unitGrams:
      return (amount)
    case unitOunces:
      return (amount / dryWeightOunceToGram)
    case unitKilos:
      return (amount / dryWeightKgToGram)
    case unitPounds:
      return (amount / dryWeightPoundToGram)
    case unitTeaspoon:
      return (amount / dryYeastTspToGram)
    case unitTablespoon:
      return (amount / dryYeastTbspToGram)
    default:
      return NaN
  }
}

// OK: Convert levain from a weight in <unitType> to a weight in grams
export function levainUnitToGrams(amount, srcUnitType) {
  // valid units: grams, oz, kilos, lbs, tsp, tbsp
  switch(srcUnitType) {
    case unitGrams:
      return amount
    case unitOunces:
      return flourUnitToGrams(amount / 2, unitOunces) + waterUnitToGrams(amount / 2, unitOunces)
    case unitKilos:
      return flourUnitToGrams(amount / 2, unitKilos) + waterUnitToGrams(amount / 2, unitKilos)
    case unitPounds:
      return flourUnitToGrams(amount / 2, unitPounds) + waterUnitToGrams(amount / 2, unitPounds)
    case unitTeaspoon:
      return flourUnitToGrams(amount / 2, unitTeaspoon) + waterUnitToGrams(amount / 2, unitTeaspoon)
    case unitTablespoon:
      return flourUnitToGrams(amount / 2, unitTablespoon) + waterUnitToGrams(amount / 2, unitTablespoon)
    default:
      return NaN
  }
}

// ROUNDING! Convert levain from a weight in grams to a weight in <unitType>
export function levainGramsToUnit(amount, destUnitType) {
  // valid units: grams, oz, kilos, lbs, tsp, tbsp
  switch(destUnitType) {
    case unitGrams:
      return Math.round(amount)
    case unitOunces:
      return toFixedDecimal(flourGramsToUnit(amount / 2, unitOunces) + waterGramsToUnit(amount / 2, unitOunces), 1)
    case unitKilos:
      return toFixedDecimal(flourGramsToUnit(amount / 2, unitKilos) + waterGramsToUnit(amount / 2, unitKilos), 3)
    case unitPounds:
      return toFixedDecimal(flourGramsToUnit(amount / 2, unitPounds) + waterGramsToUnit(amount / 2, unitPounds), 3)
    case unitTeaspoon:
      return toFixedDecimal(flourGramsToUnit(amount / 2, unitTeaspoon) + waterGramsToUnit(amount / 2, unitTeaspoon), 2)
    case unitTablespoon:
      return toFixedDecimal(flourGramsToUnit(amount / 2, unitTablespoon) + waterGramsToUnit(amount / 2, unitTablespoon), 2)
    default:
      return NaN
  }
}

// OK: convert dry weight from a weight in <unitType> to a weight in grams
export function dryWeightUnitToGrams(amount, srcUnitType) {
  // valid units: grams, oz, kilos, lbs
  switch(srcUnitType) {
    case unitGrams:
      return amount
    case unitOunces:
      return amount * dryWeightOunceToGram
    case unitKilos:
      return amount * dryWeightKgToGram
    case unitPounds:
      return amount * dryWeightPoundToGram
    default:
      return NaN
  }
}

// OK: convert dry weight from a weight in grams to a weight in <unitType>
export function dryWeightGramsToUnit(amount, destUnitType) {
  // valid units: grams, oz, kilos, lbs
  switch(destUnitType) {
    case unitGrams:
      return Math.round(amount)
    case unitOunces:
      return (amount / dryWeightOunceToGram)
    case unitKilos:
      return (amount / dryWeightKgToGram)
    case unitPounds:
      return (amount / dryWeightPoundToGram)
    default:
      return NaN
  }
}

export function toCelsius(degreesF) {
  return toFixedDecimal(((degreesF - 32) * 5) / 9, 1)
}

export function getAutolyseLiquidPct(hydrationPct) {
  let autolysePct = 95
  if (hydrationPct > 74) {
    autolysePct = (85 - (hydrationPct - 75))
  } else if (hydrationPct > 64) {
    autolysePct = (95 - (hydrationPct - 65))
  }
  return toFixedDecimal(autolysePct / 100, 2)
}